import styled, { css } from 'styled-components'
import { generateExtensionColors } from './helpers'

type ContainerProps = {
  $small?: boolean
  $video?: boolean
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  height: 100%;
  position: relative;

  > span {
    width: 100%;
    color: var(--dark-gray);
    font-size: 13px;
    font-weight: medium;
    text-align: start;

    > b {
      color: var(--red-alert);
      font-weight: 400;
    }
  }

  ${props =>
    props.$small &&
    css`
      > div {
        height: 160px !important;
      }
    `}

  ${props =>
    props.$video &&
    css`
      > div {
        padding: 0 !important;
      }
    `}
`

export const EmptyContainer = styled.nav`
  display: flex;
  align-items: center;
  width: 100%;
  flex-direction: column;
  gap: 2px;
  text-align: center;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }

  > h1 {
    font-size: 16px;
    font-weight: 600;
    color: var(--dark-gray);
  }

  > h3 {
    font-size: 14px;
    font-weight: 500;
    color: var(--dark-gray);
  }

  > span {
    font-size: 14px;
    color: var(--placeholder);
  }
`

export const EmptyContainerMinified = styled.nav<{ ext: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    height: 48px;
    border-radius: 5px 0 0 5px;
    background-color: var(--primary-opacity-25);

    > svg {
      width: 18px;
      height: 18px;
    }
  }

  > nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 6px 8px;
    background-color: white;
    border: 1px solid var(--inputs);
    border-radius: 0 5px 5px 0;
    height: 48px;
    width: 100%;

    > div {
      display: flex;
      align-items: center;
      gap: 8px;

      > button:last-child > svg {
        width: 10px;
        height: 10px;
      }

      > button {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 8px;
        border-radius: 5px;
        height: 28px;
        width: 28px;
        background-color: var(--primary-opacity-15);
        cursor: pointer;

        > svg {
          height: 8px;
          width: 8px;
        }
      }
    }

    > nav {
      display: flex;
      flex-direction: column;
      gap: 2px;
      justify-content: space-between;

      > h3 {
        font-size: 14px;
        font-weight: 500;
        color: var(--placeholder);

        @media (max-width: 660px) {
          font-size: 12px;
        }

        @media (max-width: 480px) {
          font-size: 10px;
        }
      }

      > div {
        display: flex;
        gap: 4px;

        > span {
          padding: 2px 8px;
          width: fit-content;
          border-radius: 5px;
          font-size: 10px;
          font-weight: 300;
          color: ${props => generateExtensionColors(props.ext)};
          background: ${props => generateExtensionColors(props.ext)}33;
        }

        @media (max-width: 660px) {
          display: none;
        }
      }
    }
  }
`

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 16px;
  }
`

export const FilePreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  max-width: 100%;
  max-height: 100%;
  background: #f1f1f1;
  border-radius: 4px;

  > img {
    margin: auto 0;
    max-width: 100%;
    max-height: 100%;
    border-radius: 4px;
    object-fit: contain;
    object-position: center;
  }

  > video {
    width: 100%;
    height: fit-content;
    margin: auto;
    border-radius: 4px;
  }
`

interface FilePreviewCardProps {
  ext: string
}

export const FilePreviewCard = styled.div<FilePreviewCardProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  max-width: 100%;
  overflow: hidden;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    width: 36px;
    height: 36px;
    border-radius: 2px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${props => generateExtensionColors(props.ext)};
    background: ${props => generateExtensionColors(props.ext)}33;
  }

  > span {
    font-size: 14px;
    color: var(--dark-gray);
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`

export const FullSizePreview = styled.div<FilePreviewCardProps>`
  display: flex;
  overflow: hidden;
  flex: 1;
  width: 100%;
  max-width: 100%;
  margin: auto;
  flex-direction: column;
  height: fit-content;
  border-radius: 4px;

  > div {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    width: 100%;
    min-width: 80px;
    padding: 8px;
    overflow: hidden;
    color: ${props => generateExtensionColors(props.ext)};
    background: ${props => generateExtensionColors(props.ext)}33;
  }

  > span {
    color: var(--dark-gray);
    font-size: 12px;
    padding: 6px;
    background: #f9f9f9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    max-width: 80px;
    border-radius: 0 0 4px 4px;
  }
`

export const DeleteButton = styled.div`
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background-color: var(--background);
  border: 1px solid var(--inputs);
  margin-bottom: auto;
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  margin-bottom: inherit;

  > svg > path {
    stroke: var(--placeholder);
  }
`

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  > section {
    opacity: 0;
    transition: 0.3s ease;
  }

  &:hover {
    > section {
      opacity: 1;
      transition: 0.3s ease;
    }
  }

  @media (max-width: 480px) {
    height: 100% !important;
  }
`

interface LoadImageProps {
  loaded?: boolean
}

export const LoadImage = styled.section<LoadImageProps>`
  position: absolute;
  inset: 0;
  background: var(--primary-hover);
  border-radius: 4px;
  transition: 0.5s ease;
  opacity: ${props => (props.loaded ? 0 : 1)};
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;

  > svg {
    width: 24px;
    height: 24px;
  }
`

export const Error = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  background: #cb2a2f33;
  color: #cb2a2f;
  padding: 8px 12px;
  font-size: 14px;
  border-radius: 4px;
  gap: 4px;
  min-height: 34px;

  > span {
    font-weight: 700;
  }

  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    background: #cb2a2f33;
    width: 18px;
    height: 18px;
    border-radius: 18px;
    cursor: pointer;

    > svg > path {
      stroke: #cb2a2f;
    }
  }
`

export const PreviewsContainer = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  gap: 16px;

  flex-wrap: wrap;

  > div {
    max-width: calc((100% - 16px) / 2);
  }

  @media (max-width: 480px) {
    > img {
      height: 100px;
    }
  }
`

export const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  background-color: #3c4042;
  border-radius: 4px;
  padding-top: 16px;

  > nav {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    > nav {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--primary-opacity-15);
      gap: 4px;
      padding: 8px;
      border-radius: 4px;

      font-size: 12px;
      font-weight: 500;
      color: var(--primary);

      > svg {
        width: 16px;
      }
    }
  }

  > section {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    width: 100%;

    > div {
      display: flex;
      align-items: center;
      width: 100%;
      height: 4px;
      border-radius: 4px 4px 0 0;
      background: #f2f2f226;

      > div {
        display: flex;
        width: 60%;
        height: 100%;
        background: white;
        border-radius: 0 4px 4px 0;
      }
    }

    > nav {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 32px;
      background: #ffffff0d;

      > div {
        position: absolute;
        left: 16px;
        bottom: 11px;
      }

      > p {
        font-size: 12px;
        font-weight: 500;
        color: white;
      }
    }
  }
`
